import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import { MealPlanConstraintsEditor } from './Profile';
import {
  defaultMealPlanConstraints,
  normalizeMealPlanConstraints,
  summarizeConstraints
} from '../utils/mealPlanConstraints';

function ConstraintProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`/api/constraint-profiles`);
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) {
      return;
    }

    try {
      await axios.delete(`/api/constraint-profiles/${id}`);
      fetchProfiles();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete preset');
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingProfile(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className={`${mainContentClassName} text-center`}>Loading...</div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/form-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1
        }}
      />
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-5xl mx-auto">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-8">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Constraint Presets</h1>
              <button
                onClick={handleNew}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md text-sm sm:text-base"
              >
                + New Preset
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Saved presets can be picked when generating a meal plan. Recipe usage tracking stays in your{' '}
              <Link to="/profile" className="text-amber-600 hover:underline font-semibold">Profile</Link>.
            </p>

            {showForm && (
              <PresetForm
                profile={editingProfile}
                onSave={() => {
                  setShowForm(false);
                  setEditingProfile(null);
                  fetchProfiles();
                }}
                onCancel={() => {
                  setShowForm(false);
                  setEditingProfile(null);
                }}
              />
            )}

            {!showForm && (
              <div className="space-y-4">
                {profiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    <p className="mb-4">No presets yet. Create one to reuse constraint setups when generating meal plans.</p>
                    <button
                      onClick={handleNew}
                      className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md"
                    >
                      Create Preset
                    </button>
                  </div>
                ) : (
                  profiles.map((profile) => {
                    const summary = summarizeConstraints(profile.mealPlanConstraints || {});
                    return (
                      <div key={profile._id} className="bg-white/80 p-4 sm:p-6 rounded-lg shadow">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-xl sm:text-2xl font-semibold">{profile.name}</h3>
                              {profile.isDefault && (
                                <span className="px-2 py-1 bg-amber-600 text-white text-xs rounded">Default</span>
                              )}
                            </div>
                            {profile.description && (
                              <p className="text-gray-600 mb-3">{profile.description}</p>
                            )}
                            <p className="text-sm text-gray-500">
                              {summary.rules} custom rules · default meals: {summary.mealTypes.join(', ')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(profile)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(profile._id)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function PresetForm({ profile, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isDefault: false,
    mealPlanConstraints: defaultMealPlanConstraints()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`/api/constraint-profiles/templates/list`);
        setTemplates(response.data || []);
      } catch (fetchError) {
        console.error('Error loading planning templates:', fetchError);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        description: profile.description || '',
        isDefault: profile.isDefault || false,
        mealPlanConstraints: normalizeMealPlanConstraints(profile.mealPlanConstraints || defaultMealPlanConstraints())
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const url = profile
        ? `/api/constraint-profiles/${profile._id}`
        : `/api/constraint-profiles`;
      const method = profile ? 'put' : 'post';

      await axios[method](url, formData);
      onSave();
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Failed to save preset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white/80 p-4 sm:p-6 rounded-lg">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Preset Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          rows="2"
        />
      </div>

      {templates.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Start from template</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    name: prev.name || template.name,
                    description: prev.description || template.description,
                    mealPlanConstraints: normalizeMealPlanConstraints(template.mealPlanConstraints)
                  }));
                }}
                className="text-left p-3 border border-gray-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-colors"
              >
                <p className="font-semibold text-sm">{template.name}</p>
                <p className="text-xs text-gray-600 mt-1">{template.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isDefault"
          checked={formData.isDefault}
          onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
          className="mr-2"
        />
        <label htmlFor="isDefault" className="text-sm font-medium text-gray-700">
          Set as default preset
        </label>
      </div>

      <MealPlanConstraintsEditor
        embedded
        value={formData.mealPlanConstraints}
        onChange={(next) => {
          setFormData((prev) => ({
            ...prev,
            mealPlanConstraints: typeof next === 'function' ? next(prev.mealPlanConstraints) : next
          }));
        }}
        showDefaultMealTypes
      />

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400"
        >
          {loading ? 'Saving...' : 'Save Preset'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default ConstraintProfiles;
